/**
 * fiko-makro.zengin.ts — Zengin "çarpıcı hazır ürün" makroları (saf IOzellik[] üreteçler).
 *
 * Tesisat / Takı / Çelik / Mimarlık / Marangoz workbench'leri için tek-tık parametrik ürünler.
 * Yapı taşları: kutuFeat / silindirFeat + eksen-yönlü düzlemler. Motor (rebuild) CSG ile kurar.
 * Konvansiyon: kutu/silindir taban-merkezi origin'de, düzlem normali yönünde büyür.
 */
import * as THREE from 'three';
import { ISketchDuzlem, FikoSketch } from './FikoSketch';
import { IOzellik } from './FikoModellemeYonetici';
import { IMakroParam, kutuFeat, silindirFeat, xyDuzlem } from './fiko-makro.insaat';

// Eksen-yönlü düzlemler (silindir/kutu o yönde büyür)
const zDuz = (x = 0, y = 0, z = 0): ISketchDuzlem => FikoSketch.duzlemHazir('xy', new THREE.Vector3(x, y, z)); // +Z
const yDuz = (x = 0, y = 0, z = 0): ISketchDuzlem => FikoSketch.duzlemHazir('xz', new THREE.Vector3(x, y, z)); // +Y
const xDuz = (x = 0, y = 0, z = 0): ISketchDuzlem => FikoSketch.duzlemHazir('yz', new THREE.Vector3(x, y, z)); // +X

/** Dairesel dizi: bir üretici fn(aci)→IOzellik, n adet, R yarıçap merkez (cx,cy) etrafında. */
function daireselDiz(n: number, fn: (x: number, y: number, aciDeg: number) => IOzellik, R: number, cx = 0, cy = 0, faz = 0): IOzellik[] {
  const out: IOzellik[] = [];
  for (let i = 0; i < n; i++) { const a = faz + (i / n) * Math.PI * 2; out.push(fn(cx + Math.cos(a) * R, cy + Math.sin(a) * R, a * 180 / Math.PI)); }
  return out;
}

// ─────────────────────────── TESİSAT ───────────────────────────

/** T-Boru (tee): ana boru (X) + dik branş (Y), içi boş. */
export function tBoruMakro(p: IMakroParam): IOzellik[] {
  const cap = +p['mCap'] || 40, et = +p['mEtKal'] || 4, uz = +p['mUzun'] || 160, brUz = +p['mBransUzun'] || 70;
  const ic = Math.max(1, cap - 2 * et);
  return [
    silindirFeat(cap, uz, xDuz(-uz / 2, 0, 0), true),         // ana boru (X)
    silindirFeat(cap, brUz + cap / 2, yDuz(0, 0, 0), true),   // branş (Y)
    silindirFeat(ic, uz + 4, xDuz(-uz / 2 - 2, 0, 0), false), // ana iç delik
    silindirFeat(ic, brUz + cap, yDuz(0, -2, 0), false),      // branş iç delik
  ];
}

/** Flanşlı Boru: boru + iki uçta flanş diski + cıvata delikleri (dairesel dizi). */
export function flansliBoruMakro(p: IMakroParam): IOzellik[] {
  const cap = +p['mCap'] || 50, et = +p['mEtKal'] || 5, uz = +p['mUzun'] || 140;
  const flCap = +p['mFlansCap'] || cap * 1.8, flKal = +p['mFlansKal'] || 10, civ = +p['mCivataAdet'] || 6;
  const ic = Math.max(1, cap - 2 * et), civCap = Math.max(3, cap * 0.12), civR = (flCap + cap) / 4;
  const out: IOzellik[] = [
    silindirFeat(cap, uz, zDuz(0, 0, 0), true),
    silindirFeat(flCap, flKal, zDuz(0, 0, 0), true),
    silindirFeat(flCap, flKal, zDuz(0, 0, uz - flKal), true),
    silindirFeat(ic, uz + 4, zDuz(0, 0, -2), false),
  ];
  out.push(...daireselDiz(civ, (x, y) => silindirFeat(civCap, flKal + 4, zDuz(x, y, -2), false), civR));
  out.push(...daireselDiz(civ, (x, y) => silindirFeat(civCap, flKal + 4, zDuz(x, y, uz - flKal - 2), false), civR));
  return out;
}

/** Redüksiyon: iki farklı çapta üst üste boru (koni geçişli değil, kademeli). */
export function reduksiyonMakro(p: IMakroParam): IOzellik[] {
  const buyuk = +p['mBuyukCap'] || 60, kucuk = +p['mKucukCap'] || 32, et = +p['mEtKal'] || 4, uz = +p['mUzun'] || 120;
  const h1 = uz * 0.45, h2 = uz * 0.55;
  return [
    silindirFeat(buyuk, h1, zDuz(0, 0, 0), true),
    { tip: 'primitif', ekleme: true, primitifTip: 'koni', pCap: buyuk, pYuk: 0.0001, duzlem: zDuz(0, 0, h1) } as any, // geçiş (ince)
    silindirFeat(kucuk, h2, zDuz(0, 0, h1), true),
    silindirFeat(Math.max(1, kucuk - 2 * et), uz + 4, zDuz(0, 0, -2), false),
  ];
}

// ─────────────────────────── TAKI ───────────────────────────

/** Solitaire Yüzük: band (içi boş silindir) + tırnaklar (dairesel dizi) + taş (koni baş aşağı). */
export function solitaireMakro(p: IMakroParam): IOzellik[] {
  const icCap = +p['mCap'] || 18, band = +p['mBand'] || 3, gen = +p['mUzun'] || 5;
  const tasCap = +p['mTasCap'] || 7, prong = +p['mProngAdet'] || 4;
  const disCap = icCap + 2 * band, ust = gen;
  const out: IOzellik[] = [
    silindirFeat(disCap, gen, zDuz(0, 0, 0), true),
    silindirFeat(icCap, gen + 4, zDuz(0, 0, -2), false),
  ];
  // tırnaklar (taş çevresi)
  out.push(...daireselDiz(prong, (x, y) => silindirFeat(Math.max(0.6, tasCap * 0.18), tasCap * 0.9, zDuz(x, y, ust), true), tasCap * 0.45));
  // taş: ters koni (pavyon) + üst masa
  out.push({ tip: 'primitif', ekleme: true, primitifTip: 'koni', pCap: tasCap, pYuk: tasCap * 0.8, duzlem: zDuz(0, 0, ust + tasCap * 0.8) } as any);
  return out;
}

/** Bilezik / Bangle: geniş içi boş halka (kalın band). */
export function bilezikMakro(p: IMakroParam): IOzellik[] {
  const icCap = +p['mCap'] || 60, band = +p['mBand'] || 6, gen = +p['mUzun'] || 14;
  return [
    silindirFeat(icCap + 2 * band, gen, zDuz(0, 0, 0), true),
    silindirFeat(icCap, gen + 4, zDuz(0, 0, -2), false),
  ];
}

/** Düz Boru: uzunluk boyunca içi boş silindir. */
export function boruMakro(p: IMakroParam): IOzellik[] {
  const cap = +p['mCap'] || 30, et = +p['mEtKal'] || 3, uz = +p['mUzun'] || 120;
  const ic = Math.max(1, cap - 2 * et);
  return [
    silindirFeat(cap, uz, zDuz(0, 0, 0), true),
    silindirFeat(ic, uz + 4, zDuz(0, 0, -2), false),
  ];
}

/** Dirsek (90° elbow yaklaşık): dikey + yatay iki kol, içi boş. */
export function dirsekMakro(p: IMakroParam): IOzellik[] {
  const cap = +p['mCap'] || 30, et = +p['mEtKal'] || 3, uz = +p['mUzun'] || 60;
  const ic = Math.max(1, cap - 2 * et);
  return [
    silindirFeat(cap, uz, zDuz(0, 0, 0), true),           // dikey kol (+Z)
    silindirFeat(cap, uz, xDuz(0, 0, 0), true),           // yatay kol (+X)
    silindirFeat(ic, uz + cap, zDuz(0, 0, -2), false),    // dikey iç delik
    silindirFeat(ic, uz + cap, xDuz(-2, 0, 0), false),    // yatay iç delik
  ];
}

/** Düz Yüzük (band): içi boş halka. */
export function yuzukMakro(p: IMakroParam): IOzellik[] {
  const icCap = +p['mCap'] || 18, band = +p['mBand'] || 4, gen = +p['mUzun'] || 5;
  return [
    silindirFeat(icCap + 2 * band, gen, zDuz(0, 0, 0), true),
    silindirFeat(icCap, gen + 4, zDuz(0, 0, -2), false),
  ];
}

/** Taş Yuvası (prong/tırnak): taban yuva + dairesel tırnaklar. */
export function tasYuvasiMakro(p: IMakroParam): IOzellik[] {
  const tasCap = +p['mTasCap'] || 8, adet = +p['mProngAdet'] || 4;
  const prongCap = +p['mProngCap'] || Math.max(0.6, tasCap * 0.15), prongYuk = +p['mProngYuk'] || 6;
  const tabanCap = tasCap * 1.1, tabanKal = Math.max(1, tasCap * 0.25);
  const out: IOzellik[] = [
    silindirFeat(tabanCap, tabanKal, zDuz(0, 0, 0), true),                          // taban yuva
    silindirFeat(Math.max(1, tasCap * 0.6), tabanKal + 4, zDuz(0, 0, -2), false),   // taş oturma deliği
  ];
  out.push(...daireselDiz(adet, (x, y) => silindirFeat(prongCap, prongYuk, zDuz(x, y, tabanKal), true), tasCap * 0.5));
  return out;
}

/** Bezel (çerçeve yuva): dış çeper + iç taş oyuğu (dipte taban kalır). */
export function bezelMakro(p: IMakroParam): IOzellik[] {
  const tasCap = +p['mTasCap'] || 8, et = +p['mEtKal'] || 1, yuk = +p['mProngYuk'] || 6;
  return [
    silindirFeat(tasCap + 2 * et, yuk, zDuz(0, 0, 0), true),       // dış çeper
    silindirFeat(tasCap, yuk + 2, zDuz(0, 0, yuk * 0.3), false),   // taş oyuğu (üstten)
  ];
}

// ─────────────────────────── ÇELİK ───────────────────────────

/** Bulonlu Taban Plakası: plaka + kolon güdüğü + 4 köşe bulon deliği. */
export function tabanPlakasiMakro(p: IMakroParam): IOzellik[] {
  const en = +p['en'] || 200, kal = +p['kalinlik'] || 20, kolon = +p['mKolonCap'] || 80, kolonYuk = +p['mKolonYuk'] || 120;
  const civCap = +p['mCivataCap'] || 18, kenar = en * 0.32;
  const out: IOzellik[] = [
    kutuFeat(en, en, kal, zDuz(0, 0, 0), true),
    silindirFeat(kolon, kolonYuk, zDuz(0, 0, kal), true),
  ];
  for (const sx of [-1, 1]) for (const sy of [-1, 1]) out.push(silindirFeat(civCap, kal + 4, zDuz(sx * kenar, sy * kenar, -2), false));
  return out;
}

/** Vierendeel Kafes Kiriş: alt+üst başlık + düşey dikmeler (dizi). */
export function kafesKirisMakro(p: IMakroParam): IOzellik[] {
  const uz = +p['mUzun'] || 600, yuk = +p['mYuk'] || 120, gen = +p['en'] || 40, t = +p['kalinlik'] || 8;
  const adet = Math.max(2, +p['mDikmeAdet'] || 7);
  const out: IOzellik[] = [
    kutuFeat(uz, gen, t, zDuz(0, 0, 0), true),          // alt başlık
    kutuFeat(uz, gen, t, zDuz(0, 0, yuk - t), true),    // üst başlık
  ];
  for (let i = 0; i < adet; i++) { const x = -uz / 2 + (i / (adet - 1)) * uz; out.push(kutuFeat(t, gen, yuk - 2 * t, zDuz(x, 0, t), true)); }
  return out;
}

// ─────────────────────────── MİMARLIK ───────────────────────────

/** Düz Merdiven: basamak dizisi (her biri yükselir + ilerler). */
export function duzMerdivenMakro(p: IMakroParam): IOzellik[] {
  const adet = Math.max(2, +p['mBasamakAdet'] || 12), rıht = +p['mRihtlik'] || 18, basıs = +p['mBasis'] || 28, gen = +p['en'] || 100, kal = +p['kalinlik'] || 5;
  const out: IOzellik[] = [];
  for (let i = 0; i < adet; i++) out.push(kutuFeat(basıs, gen, kal, zDuz(i * basıs, 0, i * rıht), true));
  return out;
}

/** Korkuluk: küpeşte (üst yatay) + dikme dizisi. */
export function korkulukMakro(p: IMakroParam): IOzellik[] {
  const uz = +p['mUzun'] || 300, yuk = +p['mYuk'] || 100, adet = Math.max(2, +p['mDikmeAdet'] || 8), dikme = +p['mDikmeCap'] || 4, kupeste = +p['mKupesteCap'] || 6;
  const out: IOzellik[] = [silindirFeat(kupeste, uz, xDuz(-uz / 2, 0, yuk), true)];
  for (let i = 0; i < adet; i++) { const x = -uz / 2 + (i / (adet - 1)) * uz; out.push(silindirFeat(dikme, yuk, zDuz(x, 0, 0), true)); }
  return out;
}

/** Sütun + Başlık + Kaide: yuvarlak sütun, kare başlık + kaide. */
export function sutunBaslikMakro(p: IMakroParam): IOzellik[] {
  const cap = +p['mCap'] || 50, yuk = +p['mYuk'] || 280, baslik = cap * 1.6, bh = cap * 0.4;
  return [
    kutuFeat(baslik, baslik, bh, zDuz(0, 0, 0), true),       // kaide
    silindirFeat(cap, yuk - 2 * bh, zDuz(0, 0, bh), true),   // gövde
    kutuFeat(baslik, baslik, bh, zDuz(0, 0, yuk - bh), true), // başlık
  ];
}

// ─────────────────────────── MARANGOZ ───────────────────────────

/** Masa: tabla + 4 ayak. */
export function masaMakro(p: IMakroParam): IOzellik[] {
  const en = +p['en'] || 1200, boy = +p['boy'] || 700, yuk = +p['yuk'] || 740, t = +p['kalinlik'] || 30, ayak = +p['mAyakKalin'] || 60;
  const ix = en / 2 - ayak, iy = boy / 2 - ayak;
  const out: IOzellik[] = [kutuFeat(en, boy, t, zDuz(0, 0, yuk - t), true)];
  for (const sx of [-1, 1]) for (const sy of [-1, 1]) out.push(kutuFeat(ayak, ayak, yuk - t, zDuz(sx * ix, sy * iy, 0), true));
  return out;
}

/** Kitaplık: 2 yan + üst/alt + raf dizisi + sırt. */
export function kitaplikMakro(p: IMakroParam): IOzellik[] {
  const en = +p['en'] || 800, boy = +p['boy'] || 300, yuk = +p['yuk'] || 1800, t = +p['kalinlik'] || 18, raf = Math.max(1, +p['mRafSayisi'] || 4);
  const out: IOzellik[] = [
    kutuFeat(t, boy, yuk, zDuz(-en / 2 + t / 2, 0, 0), true),  // sol yan
    kutuFeat(t, boy, yuk, zDuz(en / 2 - t / 2, 0, 0), true),   // sağ yan
    kutuFeat(en, t, yuk, zDuz(0, boy / 2 - t / 2, 0), true),   // sırt
  ];
  const icEn = en - 2 * t, n = raf + 1;
  for (let i = 0; i <= n; i++) { const z = (i / n) * (yuk - t); out.push(kutuFeat(icEn, boy - t, t, zDuz(0, -t / 2, z), true)); }
  return out;
}

/** Sandalye: oturak + 4 ayak + sırtlık. */
export function sandalyeMakro(p: IMakroParam): IOzellik[] {
  const en = +p['en'] || 420, boy = +p['boy'] || 420, otur = +p['yuk'] || 460, t = +p['kalinlik'] || 30, ayak = +p['mAyakKalin'] || 40, sirt = +p['mSirtYuk'] || 420;
  const ix = en / 2 - ayak, iy = boy / 2 - ayak;
  const out: IOzellik[] = [kutuFeat(en, boy, t, zDuz(0, 0, otur - t), true)];
  for (const sx of [-1, 1]) for (const sy of [-1, 1]) out.push(kutuFeat(ayak, ayak, otur - t, zDuz(sx * ix, sy * iy, 0), true));
  out.push(kutuFeat(en, t, sirt, zDuz(0, -boy / 2 + t / 2, otur), true)); // sırtlık (arka)
  return out;
}

/** Sehpa: yuvarlak tabla + merkezi ayak + taban. */
export function sehpaMakro(p: IMakroParam): IOzellik[] {
  const cap = +p['mCap'] || 600, yuk = +p['yuk'] || 450, t = +p['kalinlik'] || 25, ayak = +p['mAyakCap'] || 80;
  return [
    silindirFeat(cap, t, zDuz(0, 0, yuk - t), true),
    silindirFeat(ayak, yuk - t, zDuz(0, 0, 0), true),
    silindirFeat(cap * 0.6, t, zDuz(0, 0, 0), true),
  ];
}

/** Zengin makro üreteçleri (workbench → ürün). */
export const MAKRO_URETECLER_ZENGIN: Record<string, (p: IMakroParam) => IOzellik[]> = {
  tBoru: tBoruMakro, flansliBoru: flansliBoruMakro, reduksiyon: reduksiyonMakro,
  solitaire: solitaireMakro, bilezik: bilezikMakro,
  // Eski registry komutları için eksik executor'lar (boru/dirsek/yuzuk/tasYuvasi/bezel) — artık çalışır
  boru: boruMakro, dirsek: dirsekMakro, yuzuk: yuzukMakro, tasYuvasi: tasYuvasiMakro, bezel: bezelMakro,
  tabanPlakasi: tabanPlakasiMakro, kafesKiris: kafesKirisMakro,
  duzMerdiven: duzMerdivenMakro, korkuluk: korkulukMakro, sutunBaslik: sutunBaslikMakro,
  masa: masaMakro, kitaplik: kitaplikMakro, sandalye: sandalyeMakro, sehpa: sehpaMakro,
};
